'use client';

import { useSuspenseQuery } from '@apollo/client/react';
import { FC, PropsWithChildren, useMemo } from 'react';

import { GetPublicationsQuery } from '@/.gql/graphql';
import { GET_PUBLICATIONS } from '@/.graphql';

import { createCustomContext } from './context';

interface IPublicationsProps {
  publications: GetPublicationsQuery['getPublications']['data'];
  hasMorePublications: boolean;
  isLoadingPublications: boolean;
  loadMorePublications: () => void;
}

const [PublicationsContext, usePublicationsContext] =
  createCustomContext<IPublicationsProps>({
    name: 'PublicationsContext',
    strict: false,
  });

export { usePublicationsContext };

export const PublicationsProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    data: imageData,
    fetchMore: fetchMorePublicationss,
    networkStatus,
  } = useSuspenseQuery(GET_PUBLICATIONS, {
    variables: {
      cursor: null,
    },
  });

  const publications = useMemo(
    () => imageData?.getPublications?.data || [],
    [imageData],
  );

  const hasMorePublications = !!imageData?.getPublications.nextCursor;

  const isLoadingPublications = networkStatus === 3;
  const loadMorePublications = useMemo(
    () => () => {
      if (!hasMorePublications || isLoadingPublications) {
        return;
      }

      return fetchMorePublicationss({
        variables: {
          cursor: imageData?.getPublications.nextCursor,
        },
      });
    },
    [
      hasMorePublications,
      isLoadingPublications,
      imageData,
      fetchMorePublicationss,
    ],
  );

  const contextValue = useMemo(
    () => ({
      publications,
      hasMorePublications,
      loadMorePublications,
      isLoadingPublications,
    }),
    [
      publications,
      hasMorePublications,
      loadMorePublications,
      isLoadingPublications,
    ],
  );

  return (
    <PublicationsContext value={contextValue}>{children}</PublicationsContext>
  );
};
